/**
 * `/liturgies/{id}/attachments` — supporting material.
 *
 * From the mockup's `isFiles` block, including the two sentences that decide how it behaves:
 *
 * > Structured Planning remains the authoritative representation of this Liturgy — nothing here
 * > replaces a Planning Item.
 *
 * > A file that failed validation offers **no download control at all** rather than a disabled one.
 *
 * The second is not a styling preference. A greyed-out Download implies "not yet, try later", which
 * is true of a pending scan and false of an infected file. Rendering nothing says the only accurate
 * thing (docs/engineering/11-screens.md).
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';

import { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { WorkspaceTabs } from '../../components/composites/WorkspaceTabs';
import {
  downloadUrl,
  fetchAttachments,
  isPending,
  removeAttachment,
  uploadAttachment,
  type Attachment,
  type ScanState,
} from './api';
import './attachments.css';

const STATE_LABEL: Record<ScanState, string> = {
  uploaded: 'Awaiting scan',
  pending_scan: 'Scanning',
  clean: 'Available',
  infected: 'Rejected',
  scan_failed: 'Unavailable',
};

const STATE_TONE: Record<ScanState, string> = {
  uploaded: 'att-pill--waiting',
  pending_scan: 'att-pill--waiting',
  clean: 'att-pill--clean',
  infected: 'att-pill--blocked',
  scan_failed: 'att-pill--blocked',
};

export function AttachmentsScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/liturgies/$id/attachments' });
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<ApiError | null>(null);

  const attachments = useQuery({
    queryKey: ['attachments', id],
    queryFn: () => fetchAttachments(id),
    // Poll only while something is actually being scanned, and stop the moment nothing is.
    // A screen that polls forever is a screen that keeps a laptop awake for no reason.
    refetchInterval: (query) =>
      query.state.data?.data.some((a) => isPending(a.scan_state)) ? 3000 : false,
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadAttachment(id, file),
    onMutate: () => setUploadError(null),
    onError: (error) => setUploadError(error instanceof ApiError ? error : null),
    onSuccess: async () => {
      if (fileInput.current) fileInput.current.value = '';
      await queryClient.invalidateQueries({ queryKey: ['attachments', id] });
    },
  });

  const remove = useMutation({
    mutationFn: (attachmentId: string) => removeAttachment(id, attachmentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['attachments', id] });
    },
  });

  return (
    <div className="att">
      <header className="att__head">
        <div>
          <h1 className="att__title">{t('attachments.title')}</h1>
          <p className="att__lede">
            Supporting material. Structured Planning remains the authoritative representation of
            this Liturgy — nothing here replaces a Planning Item.
          </p>
        </div>
        <Link className="att__link" to="/liturgies/$id/planning" params={{ id }}>
          {t('workspace.planning')} →
        </Link>
      </header>

      <WorkspaceTabs liturgyId={id} />

      <section className="att__panel">
        <div className="att__upload">
          <input
            ref={fileInput}
            type="file"
            className="att__file"
            disabled={upload.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
            }}
          />
          <p className="att__upload-note">
            Every file is checked for malware before it becomes available. Size limits are enforced
            as the file uploads. Ownership and visibility are inherited from this Liturgy.
          </p>
        </div>

        {uploadError ? (
          <div className="att__error">
            <Notice referenceId={uploadError.correlationId}>{uploadError.message}</Notice>
          </div>
        ) : null}

        {attachments.isPending ? (
          <p className="att__note">{t('attachments.loading')}</p>
        ) : attachments.isError ? (
          <div className="att__error">
            <Notice referenceId={(attachments.error as ApiError).correlationId}>
              {(attachments.error as ApiError).message}
            </Notice>
          </div>
        ) : attachments.data.data.length === 0 ? (
          <p className="att__note">{t('attachments.empty')}</p>
        ) : (
          attachments.data.data.map((file) => (
            <AttachmentRow
              key={file.id}
              liturgyId={id}
              file={file}
              onRemove={() => remove.mutate(file.id)}
              removing={remove.isPending}
            />
          ))
        )}

        <p className="att__note">
          A file that failed validation offers no download control at all rather than a disabled
          one. Attachments follow the lifecycle of this Liturgy; history preserves upload,
          replacement and removal with the uploader.
        </p>
      </section>
    </div>
  );
}

function AttachmentRow({
  liturgyId,
  file,
  onRemove,
  removing,
}: {
  liturgyId: string;
  file: Attachment;
  onRemove: () => void;
  removing: boolean;
}): React.JSX.Element {
  return (
    <div className="att__row">
      <div className="att__meta">
        <div className="att__name">{file.display_name}</div>
        <div className="att__facts">
          {formatBytes(file.byte_size)}
          {file.uploaded_by ? ` · ${file.uploaded_by}` : null}
          {` · ${file.created_at.slice(0, 10)}`}
        </div>
        {/* Named, so someone who uploaded a rejected file knows whether to wait or to act. */}
        {file.unavailable_reason ? (
          <div
            className={`att__reason${file.scan_state === 'infected' ? ' att__reason--blocked' : ''}`}
          >
            {file.unavailable_reason}
          </div>
        ) : null}
      </div>

      <div className="att__state">
        <span className={`att-pill ${STATE_TONE[file.scan_state]}`}>
          {STATE_LABEL[file.scan_state]}
        </span>
      </div>

      <div className="att__acts">
        {/* No control at all unless the server says the file is available. Not a disabled one. */}
        {file.available ? (
          <a
            className="att__btn"
            href={downloadUrl(liturgyId, file.id)}
            download={file.display_name}
          >
            Download
          </a>
        ) : null}
        <button
          type="button"
          className="att__btn att__btn--quiet"
          disabled={removing}
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
